// The saved layout of one dashboard for one user (SEP-296).
//
// A dashboard is an ordered list of cards on a grid of 12 columns. The order of
// the `cards` array is the order on the screen. The browser places each card,
// so the layout holds no x and no y position. A hidden card keeps its last
// width, its last height and its index `at`, so that Show puts it back in the
// same place.
//
// This shape is view state of one user. It is not domain state, and it is not
// an Object Type. The server stores it in the firm infrastructure table
// `user_dashboard_layouts`.

import { z } from 'zod';

import type { Persona } from '../persona-model';

/** The width of a card in grid columns. The UI shows 1/4, 1/3, 1/2, 2/3 and 1. */
export const cardSpanSchema = z.union([
    z.literal(3),
    z.literal(4),
    z.literal(6),
    z.literal(8),
    z.literal(12),
]);

/**
 * The height of a card in grid rows of 48px, or `'fit'`. A table uses `'fit'`:
 * the web calculates the rows from the row count that the module gives.
 */
export const cardRowsSchema = z.union([z.number().int().min(1).max(12), z.literal('fit')]);
export type CardRows = z.infer<typeof cardRowsSchema>;

/** One card on the dashboard. `module` is the key of a module of the dashboard. */
export const layoutCardSchema = z.object({
    module: z.string().min(1).max(64),
    span: cardSpanSchema,
    rows: cardRowsSchema,
});
export type LayoutCard = z.infer<typeof layoutCardSchema>;

/** One hidden card. `at` is the index of the card before the user hid it. */
export const hiddenCardSchema = layoutCardSchema.extend({
    at: z.number().int().min(0).max(100),
});
export type HiddenCard = z.infer<typeof hiddenCardSchema>;

/**
 * The full layout of one dashboard. A module key occurs one time only, in
 * `cards` or in `hidden`. `template` is the key of the template that the user
 * applied, or null after a manual change.
 */
export const dashboardLayoutSchema = z
    .object({
        version: z.literal(1),
        template: z.string().max(64).nullable(),
        cards: z.array(layoutCardSchema).max(40),
        hidden: z.array(hiddenCardSchema).max(40),
    })
    .refine(
        (layout) => {
            const keys = [...layout.cards, ...layout.hidden].map((card) => card.module);
            return new Set(keys).size === keys.length;
        },
        { message: 'Each module key must occur one time only, in cards or in hidden.' },
    );
export type DashboardLayout = z.infer<typeof dashboardLayoutSchema>;

/** The dashboards that have an editable layout. */
export const dashboardSurfaceSchema = z.enum([
    'desk_dashboard',
    'ops_dashboard',
    'comply_dashboard',
    'view_dashboard',
]);
export type DashboardSurface = z.infer<typeof dashboardSurfaceSchema>;

/**
 * The personas that own each surface. The server uses the map to refuse a
 * surface that the active persona of the actor does not own.
 */
// Each key is a VALUE of `dashboardSurfaceSchema`, and a value can be
// snake_case. The four keys have an entry in `ALLOWED_SNAKE_IDENTIFIERS` of the
// snake-case guard (`apps/server/src/lib/lint/snake-case-guard.ts`).
export const SURFACE_PERSONAS: Record<DashboardSurface, readonly Persona[]> = {
    desk_dashboard: ['desk', 'im-assistant'],
    ops_dashboard: ['ops'],
    comply_dashboard: ['comply'],
    view_dashboard: ['view'],
};

/**
 * The read of one layout. `present` is false when the user has no saved
 * layout: then `layout` and `updatedAt` are null, and the web shows the
 * default layout of the surface.
 */
export const dashboardLayoutReadSchema = z.object({
    surface: dashboardSurfaceSchema,
    present: z.boolean(),
    layout: dashboardLayoutSchema.nullable(),
    updatedAt: z.string().nullable(),
});
export type DashboardLayoutRead = z.infer<typeof dashboardLayoutReadSchema>;

/** The body of a save. */
export const putDashboardLayoutBodySchema = z.object({ layout: dashboardLayoutSchema });
export type PutDashboardLayoutBody = z.infer<typeof putDashboardLayoutBodySchema>;

/** The answer to a delete. */
export const deleteDashboardLayoutResponseSchema = z.object({
    surface: dashboardSurfaceSchema,
    present: z.literal(false),
});
export type DeleteDashboardLayoutResponse = z.infer<typeof deleteDashboardLayoutResponseSchema>;
